import { useLocalization } from "@fluent/react";
import "./App.css";
import "./lib/sentry";
import { useEffect } from "react";
import { RouterProvider, createBrowserRouter } from "react-router";
import { ErrorScreen } from "@lending/components/ErrorScreen/ErrorScreen";
import LendingGoogleTagManager from "@lending/components/LendingGoogleTagManager/LendingGoogleTagManager";
import LendingMixpanel from "@lending/components/LendingMixpanel/LendingMixpanel";
import LendingNeuroID from "@lending/components/LendingNeuroID/LendingNeuroID";
import LendingSentry from "@lending/components/LendingSentry/LendingSentry";
import { useGetInstitutionQuery } from "@lending/hooks/queries/useGetInstitutionQuery";
import { LendingProviders } from "./LendingProviders";
import { getLendingRoutes } from "./routes";

const router = createBrowserRouter(getLendingRoutes());

// Renderless component — must be a child of LocalizationContent to access Fluent translations
function DocumentTitle() {
  const { l10n } = useLocalization();
  useEffect(() => {
    document.title = l10n.getString("document-title", null, "Apply for a loan");
  }, [l10n]);
  return null;
}

/**
 * The standalone lending app composition: institution theming/favicon,
 * analytics, and the browser router. These are intentionally NOT part of
 * `LendingProviders` because a host (azul) owns its own chrome, theming, and
 * router.
 */
function StandaloneApp() {
  const { institution } = useGetInstitutionQuery();

  useEffect(() => {
    const faviconUrl = institution.favicon_url;
    if (faviconUrl) {
      const link = document.createElement("link");
      link.rel = "icon";
      link.type = "image/x-icon";
      link.href = faviconUrl;
      document.head.appendChild(link);
    }

    document.documentElement.style.setProperty("--theme-primary", institution.primary_color_hex);
    document.documentElement.style.setProperty(
      "--theme-secondary",
      institution.secondary_color_hex
    );
    document.documentElement.style.setProperty("--theme-tertiary", institution.tertiary_color_hex);
    document.documentElement.style.setProperty(
      "--theme-rgb-primary",
      `${institution.primary_color}`
    );
    document.documentElement.style.setProperty(
      "--theme-rgb-secondary",
      `${institution.secondary_color}`
    );
    document.documentElement.style.setProperty(
      "--theme-rgb-tertiary",
      `${institution.tertiary_color}`
    );
  }, [institution]);

  if (institution && !institution.features?.lending) {
    return <ErrorScreen />;
  }

  return (
    <>
      <DocumentTitle />
      <LendingMixpanel institution={institution} />
      <LendingSentry />
      <LendingNeuroID institution={institution} />
      <LendingGoogleTagManager institution={institution} router={router} />
      <RouterProvider router={router} />
    </>
  );
}

function App() {
  return (
    <LendingProviders>
      <StandaloneApp />
    </LendingProviders>
  );
}

export default App;
